// Production Planning — Feb 28 2026.
//
// Per-part × per-operation plan. Inputs: cycle time, setup time,
// efficiency, operators. Machine dropdown is sourced from
// Machine Master — the machine's type + hour rate flow into the
// form automatically and cannot be typed manually.
//
// Derived (client-side preview, server re-computes on save):
//   • output_per_hour   = (60 / cycle_time_min) × efficiency
//   • cost_of_operation = machine_hour_rate / output_per_hour

import { useState, useEffect, useMemo } from "react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import Layout from "../components/Layout";
import SearchableSelect from "../components/SearchableSelect";
import MasterExcelToolbar from "../components/MasterExcelToolbar";
import { Plus, PencilSimple, Trash, ClipboardText, MagnifyingGlass, Calculator } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const blankForm = () => ({
  part_id: "",
  part_no: "",
  part_name: "",
  customer: "",
  operation: "",
  operation_name: "",
  cycle_time_min: "",
  setup_time: "",
  efficiency: "",           // stored 0..1; input % converted on save
  number_of_operators: "",
  machine_id: "",
});

// Small utility: percent → 0..1 & back
const pctToFloat = (p) => {
  const v = parseFloat(p);
  if (isNaN(v)) return 0;
  return v > 1 ? v / 100 : v;   // treat >1 as %, ≤1 as ratio
};
const floatToPct = (f) => {
  const v = parseFloat(f);
  if (isNaN(v)) return "";
  return (v * 100).toString();
};

const nfmt = (v, d = 2) => {
  const n = Number(v);
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });
};

export default function ProductionPlanning() {
  const [rows, setRows] = useState([]);
  const [parts, setParts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Inline "add operation" dialog stacked over the New-Plan modal so
  // the user can register a new step (e.g. "Heat Treatment") without
  // losing the row they were building.
  const [opDialogOpen, setOpDialogOpen] = useState(false);
  const [newOpName, setNewOpName] = useState("");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pp, pm, cm, mm, om] = await Promise.all([
        axios.get(`${API}/production-planning`),
        axios.get(`${API}/parts`),
        axios.get(`${API}/customer-master`),
        axios.get(`${API}/machine-master`),
        axios.get(`${API}/operations-master`),
      ]);
      setRows(pp.data || []);
      setParts(pm.data || []);
      setCustomers(cm.data || []);
      setMachines(mm.data || []);
      setOperations(om.data || []);
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);
  // Refresh on external changes so newly-added machines / parts show up.
  useEffect(() => {
    const h = () => loadAll();
    window.addEventListener("machines:changed", h);
    window.addEventListener("customers:changed", h);
    return () => {
      window.removeEventListener("machines:changed", h);
      window.removeEventListener("customers:changed", h);
    };
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...blankForm(), operation: operations[0]?.name || "" });
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      part_id: r.part_id || "",
      part_no: r.part_no || "",
      part_name: r.part_name || "",
      customer: r.customer || "",
      operation: r.operation || (operations[0]?.name || ""),
      operation_name: r.operation_name || "",
      cycle_time_min: r.cycle_time_min ?? "",
      setup_time: r.setup_time ?? "",
      efficiency: floatToPct(r.efficiency),
      number_of_operators: r.number_of_operators ?? "",
      machine_id: r.machine_id || "",
    });
    setDialogOpen(true);
  };

  // Selected snapshots — used for auto-fill + preview calculations.
  const selectedPart = useMemo(
    () => parts.find(p => p.id === form.part_id) || null,
    [parts, form.part_id]
  );
  const selectedMachine = useMemo(
    () => machines.find(m => m.id === form.machine_id) || null,
    [machines, form.machine_id]
  );

  // Client-side preview of derived values — server re-computes on save.
  const outputPerHour = useMemo(() => {
    const ct = parseFloat(form.cycle_time_min);
    const eff = pctToFloat(form.efficiency);
    if (!ct || ct <= 0) return 0;
    return (60 / ct) * eff;
  }, [form.cycle_time_min, form.efficiency]);

  const costOfOperation = useMemo(() => {
    if (!selectedMachine || outputPerHour <= 0) return 0;
    const mhr = parseFloat(selectedMachine.machine_hour_rate) || 0;
    return mhr / outputPerHour;
  }, [selectedMachine, outputPerHour]);

  const save = async () => {
    if (!form.part_id) return toast.error("Please pick a Part");
    if (!form.customer.trim()) return toast.error("Please pick a Customer");
    if (!form.machine_id) return toast.error("Please pick a Machine Number");
    if (!form.operation) return toast.error("Please pick an Operation");
    const ct = parseFloat(form.cycle_time_min);
    if (isNaN(ct) || ct <= 0) return toast.error("Cycle Time must be greater than zero");

    const payload = {
      part_id: form.part_id,
      part_no: selectedPart?.part_no || form.part_no || "",
      part_name: selectedPart?.description || form.part_name || "",
      customer: form.customer,
      operation: form.operation,
      operation_name: form.operation_name || "",
      cycle_time_min: parseFloat(form.cycle_time_min) || 0,
      setup_time: parseFloat(form.setup_time) || 0,
      efficiency: pctToFloat(form.efficiency),
      number_of_operators: parseInt(form.number_of_operators, 10) || 0,
      machine_id: form.machine_id,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/production-planning/${editingId}`, payload);
        toast.success("Production plan updated");
      } else {
        await axios.post(`${API}/production-planning`, payload);
        toast.success("Production plan added");
      }
      setDialogOpen(false); setForm(blankForm()); setEditingId(null); loadAll();
    } catch (e) { toast.error("Save failed: " + (e.response?.data?.detail || e.message)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this production plan row?")) return;
    try {
      await axios.delete(`${API}/production-planning/${id}`);
      toast.success("Deleted"); loadAll();
    } catch { toast.error("Delete failed"); }
  };

  const addOperation = async () => {
    const name = newOpName.trim();
    if (!name) return toast.error("Operation name required");
    try {
      await axios.post(`${API}/operations-master`, { name });
      toast.success(`Added '${name}'`);
      const om = await axios.get(`${API}/operations-master`);
      setOperations(om.data || []);
      setForm(f => ({ ...f, operation: name }));
      setNewOpName(""); setOpDialogOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add operation"); }
  };

  const filtered = rows.filter(r => {
    if (flaggedOnly && !r.has_issues) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.part_no || "").toLowerCase().includes(q) ||
           (r.part_name || "").toLowerCase().includes(q) ||
           (r.customer || "").toLowerCase().includes(q) ||
           (r.machine_number || "").toLowerCase().includes(q) ||
           (r.operation || "").toLowerCase().includes(q);
  });

  const flaggedCount = rows.filter(r => r.has_issues).length;

  // Fast lookup for "which field on THIS row is broken?" → drives the pink
  // cell tinting in the table. Returns a Set of field names.
  const issueFields = (r) => new Set((r?.issues || []).map(i => i.field));
  const cellPink = "bg-[#FCE7F3] text-[#9D174D] font-semibold";

  const hasMachines = machines.length > 0;

  // Dropdown option lists (searchable). Labels + hints let the user
  // find a part by number OR description, a customer by name, and a
  // machine by number OR type.
  const partNoOptions   = useMemo(
    () => parts.map(p => ({ value: p.id, label: p.part_no || "—", hint: p.description || "" })),
    [parts]
  );
  const partNameOptions = useMemo(
    () => parts.map(p => ({ value: p.id, label: p.description || p.part_no || "—", hint: p.part_no || "" })),
    [parts]
  );
  const customerOptions = useMemo(
    () => customers.map(c => ({ value: c.name, label: c.name })),
    [customers]
  );
  const machineOptions  = useMemo(
    () => machines.map(m => ({ value: m.id, label: m.machine_number,
      hint: `${m.machine_type}${m.machine_group ? ` · ${m.machine_group}` : ""} · ₹${Number(m.machine_hour_rate || 0).toLocaleString("en-IN")}/hr` })),
    [machines]
  );

  const pickPart = (partId) => {
    const p = parts.find(x => x.id === partId);
    setForm(f => ({
      ...f, part_id: partId,
      part_no: p?.part_no || "",
      part_name: p?.description || "",
      customer: p?.customer || f.customer,
    }));
  };

  return (
    <Layout title="Production Planning">
      <div className="p-6 space-y-5 max-w-[1600px] mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-[#1D2951]/5 text-[#1D2951]">
              <ClipboardText size={20} weight="fill" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: "Chivo" }}>
                Production Planning
              </h1>
              <p className="text-xs text-[#64748B] mt-0.5 max-w-3xl">
                Per-part × per-operation plan. Cycle time, efficiency and machine choice compute
                Output/hour and Cost/piece automatically.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <MasterExcelToolbar basePath="/production-planning" entityLabel="Plans"
              onImportDone={loadAll} size="sm" />
            <Button className="bg-[#1D2951] hover:bg-[#2A3B73] text-white gap-1 h-9"
              onClick={openAdd} data-testid="pp-add-btn"
              disabled={!hasMachines}
              title={hasMachines ? "" : "Add at least one machine in Machine Master first"}>
              <Plus size={14} /> New Plan
            </Button>
          </div>
        </div>

        {!hasMachines && (
          <Card className="p-4 border border-[#FBBF24] bg-[#FEF3C7] text-[13px] text-[#78350F]">
            You haven&apos;t added any machines yet. Go to{" "}
            <a href="/machine-master" className="font-bold underline">Masters → Machine Master</a>{" "}
            to add machines before creating a production plan.
          </Card>
        )}

        <Card className="p-2 border border-[#E2E8F0] flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by part, customer, machine, operation…"
              className="min-h-[38px] pl-9 border-transparent focus-visible:border-[#CBD5E1]"
              data-testid="pp-search" />
          </div>
          {flaggedCount > 0 && (
            <button onClick={() => setFlaggedOnly(v => !v)}
              className={`text-[11px] font-bold px-2.5 py-1.5 rounded border transition ${
                flaggedOnly
                  ? "bg-[#9D174D] text-white border-[#9D174D]"
                  : "bg-[#FCE7F3] text-[#9D174D] border-[#F9A8D4] hover:bg-[#FBCFE8]"
              }`}
              title="Show only rows with mismatched Part / Machine / Operation"
              data-testid="pp-flagged-toggle">
              {flaggedOnly ? "✓ " : ""}{flaggedCount} flagged
            </button>
          )}
          <span className="text-[11px] text-[#64748B] font-semibold pr-2">
            {filtered.length} / {rows.length} plans
          </span>
        </Card>

        <Card className="border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="pp-table">
              <thead className="bg-[#F8FAFC] text-[#475569] text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold">Part No</th>
                  <th className="px-3 py-3 text-left font-semibold">Part Name</th>
                  <th className="px-3 py-3 text-left font-semibold">Customer</th>
                  <th className="px-3 py-3 text-left font-semibold">Operation</th>
                  <th className="px-3 py-3 text-left font-semibold">Op Name</th>
                  <th className="px-3 py-3 text-left font-semibold">Machine No</th>
                  <th className="px-3 py-3 text-left font-semibold">Type</th>
                  <th className="px-3 py-3 text-left font-semibold">Group</th>
                  <th className="px-3 py-3 text-right font-semibold">CT (min)</th>
                  <th className="px-3 py-3 text-right font-semibold">Setup (min)</th>
                  <th className="px-3 py-3 text-right font-semibold">Eff %</th>
                  <th className="px-3 py-3 text-right font-semibold">Op/hr</th>
                  <th className="px-3 py-3 text-right font-semibold">Ops</th>
                  <th className="px-3 py-3 text-right font-semibold">MHR (₹)</th>
                  <th className="px-3 py-3 text-right font-semibold">Cost/pc (₹)</th>
                  <th className="px-3 py-3 text-center font-semibold w-20">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {loading && <tr><td colSpan={16} className="py-10 text-center text-[#64748B]">Loading…</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={16} className="py-12 text-center text-[#94A3B8]">
                    No production plans yet — click <b>New Plan</b> to add one.
                  </td></tr>
                )}
                {filtered.map(r => {
                  const bad = issueFields(r);
                  const issueTitle = (r.issues || []).map(i => `• ${i.reason}`).join("\n");
                  return (
                  <tr key={r.id}
                    className={`hover:bg-[#F8FAFC] ${r.has_issues ? "bg-[#FDF2F8]/60" : ""}`}
                    title={r.has_issues ? `Row has issues — click Edit to fix:\n${issueTitle}` : ""}
                    data-testid={`pp-row-${r.id}`}>
                    <td className={`px-3 py-2 font-mono font-semibold ${bad.has("part_no") ? cellPink : "text-[#0F172A]"}`}>{r.part_no}</td>
                    <td className={`px-3 py-2 max-w-[180px] truncate ${bad.has("part_no") ? cellPink : "text-[#334155]"}`} title={r.part_name}>{r.part_name}</td>
                    <td className="px-3 py-2 text-[#334155]">{r.customer || "—"}</td>
                    <td className={`px-3 py-2 ${bad.has("operation") ? cellPink : "text-[#334155]"}`}>{r.operation}</td>
                    <td className="px-3 py-2 text-[#334155] max-w-[160px] truncate" title={r.operation_name}>{r.operation_name || "—"}</td>
                    <td className={`px-3 py-2 font-mono ${bad.has("machine_no") ? cellPink : "text-[#0F172A]"}`}>{r.machine_number || "—"}</td>
                    <td className="px-3 py-2">
                      {(() => {
                        const t = (r.machine_type || "").toUpperCase();
                        const palette = {
                          CNC: "bg-[#DBEAFE] text-[#1D4ED8]",
                          VMC: "bg-[#FCE7F3] text-[#9D174D]",
                          VTL: "bg-[#DCFCE7] text-[#166534]",
                        };
                        const cls = palette[t] || "bg-[#F1F5F9] text-[#334155]";
                        return (
                          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>
                            {t || "—"}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-[#334155] text-[11px]">{r.machine_group || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{nfmt(r.cycle_time_min)}</td>
                    <td className="px-3 py-2 text-right font-mono">{nfmt(r.setup_time)}</td>
                    <td className="px-3 py-2 text-right font-mono">{nfmt((r.efficiency || 0) * 100, 1)}%</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-[#065F46]">{nfmt(r.output_per_hour, 2)}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.number_of_operators ?? 0}</td>
                    <td className="px-3 py-2 text-right font-mono">₹ {nfmt(r.machine_hour_rate)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-[#7C2D12]">₹ {nfmt(r.cost_of_operation)}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <Button size="sm" variant={r.has_issues ? "default" : "ghost"}
                        onClick={() => openEdit(r)}
                        className={r.has_issues ? "bg-[#9D174D] hover:bg-[#831843] text-white h-7 px-2" : ""}
                        data-testid={`pp-edit-${r.id}`}>
                        <PencilSimple size={14} />
                        {r.has_issues && <span className="ml-1 text-[10px] font-bold">FIX</span>}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-[#DC2626]"
                        data-testid={`pp-del-${r.id}`}>
                        <Trash size={14} />
                      </Button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Production Plan" : "New Production Plan"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Identity */}
            <div>
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#64748B] mb-2">Part & Customer</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-[11px] font-semibold text-[#334155]">Part Number *</Label>
                  <div className="mt-0.5">
                    <SearchableSelect value={form.part_id}
                      onChange={pickPart}
                      options={partNoOptions}
                      placeholder="Search part number…"
                      testId="pp-part-id" />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] font-semibold text-[#334155]">Part Name</Label>
                  <div className="mt-0.5">
                    <SearchableSelect value={form.part_id}
                      onChange={pickPart}
                      options={partNameOptions}
                      placeholder="Search part name…"
                      testId="pp-part-name" />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] font-semibold text-[#334155]">Customer *</Label>
                  <div className="mt-0.5">
                    <SearchableSelect value={form.customer}
                      onChange={(v) => setForm({ ...form, customer: v })}
                      options={customerOptions}
                      placeholder="Search customer…"
                      testId="pp-customer" />
                  </div>
                </div>
              </div>
            </div>

            {/* Operation */}
            <div>
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#64748B] mb-2">Operation</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-[11px] font-semibold text-[#334155]">Operation *</Label>
                  <div className="flex gap-1 mt-0.5">
                    <select value={form.operation}
                      onChange={e => setForm({ ...form, operation: e.target.value })}
                      className="flex-1 min-h-[38px] border border-[#E2E8F0] rounded px-2 text-xs bg-white"
                      data-testid="pp-operation">
                      <option value="">— Pick an operation —</option>
                      {operations.map(o => <option key={o.id || o.name} value={o.name}>{o.name}</option>)}
                    </select>
                    <Button size="sm" variant="outline"
                      onClick={() => { setNewOpName(""); setOpDialogOpen(true); }}
                      title="Add a new operation"
                      data-testid="pp-add-op-inline">
                      <Plus size={14} />
                    </Button>
                  </div>
                </div>
                <div className="col-span-2">
                  <Label className="text-[11px] font-semibold text-[#334155]">Operation Name</Label>
                  <Input value={form.operation_name}
                    onChange={e => setForm({ ...form, operation_name: e.target.value })}
                    placeholder="e.g. Rough turning, Slotting…"
                    className="mt-0.5"
                    data-testid="pp-operation-name" />
                </div>
              </div>
            </div>

            {/* Machine — Type + Group + MHR auto-filled */}
            <div>
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#64748B] mb-2">Machine (from Machine Master)</h3>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label className="text-[11px] font-semibold text-[#334155]">Machine Number *</Label>
                  <div className="mt-0.5">
                    <SearchableSelect value={form.machine_id}
                      onChange={(v) => setForm({ ...form, machine_id: v })}
                      options={machineOptions}
                      placeholder="Search machine number…"
                      testId="pp-machine-id" />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] font-semibold text-[#334155]">Type of Machine</Label>
                  <Input value={selectedMachine?.machine_type || ""}
                    readOnly disabled
                    placeholder="Auto from Machine Master"
                    className="mt-0.5 bg-[#F1F5F9] font-mono"
                    data-testid="pp-machine-type" />
                </div>
                <div>
                  <Label className="text-[11px] font-semibold text-[#334155]">Group</Label>
                  <Input value={selectedMachine?.machine_group || ""}
                    readOnly disabled
                    placeholder="Auto from Machine Master"
                    className="mt-0.5 bg-[#F1F5F9]"
                    data-testid="pp-machine-group" />
                </div>
                <div>
                  <Label className="text-[11px] font-semibold text-[#334155]">Machine Hour Rate (₹)</Label>
                  <Input value={selectedMachine ? `₹ ${nfmt(selectedMachine.machine_hour_rate)}` : ""}
                    readOnly disabled
                    placeholder="Auto from Machine Master"
                    className="mt-0.5 bg-[#F1F5F9] font-mono"
                    data-testid="pp-machine-hour-rate" />
                </div>
              </div>
            </div>

            {/* Timing + Efficiency + Operators */}
            <div>
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#64748B] mb-2">Timing & Manpower</h3>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label className="text-[11px] font-semibold text-[#334155]">Cycle Time (min) *</Label>
                  <Input type="number" step="any" min="0" value={form.cycle_time_min}
                    onChange={e => setForm({ ...form, cycle_time_min: e.target.value })}
                    placeholder="e.g. 2.5"
                    className="font-mono mt-0.5"
                    data-testid="pp-cycle-time" />
                </div>
                <div>
                  <Label className="text-[11px] font-semibold text-[#334155]">Setup Time (min)</Label>
                  <Input type="number" step="any" min="0" value={form.setup_time}
                    onChange={e => setForm({ ...form, setup_time: e.target.value })}
                    placeholder="e.g. 30"
                    className="font-mono mt-0.5"
                    data-testid="pp-setup-time" />
                </div>
                <div>
                  <Label className="text-[11px] font-semibold text-[#334155]">Efficiency (%)</Label>
                  <Input type="number" step="any" min="0" max="100" value={form.efficiency}
                    onChange={e => setForm({ ...form, efficiency: e.target.value })}
                    placeholder="e.g. 85"
                    className="font-mono mt-0.5"
                    data-testid="pp-efficiency" />
                </div>
                <div>
                  <Label className="text-[11px] font-semibold text-[#334155]">Number of Operators</Label>
                  <Input type="number" step="1" min="0" value={form.number_of_operators}
                    onChange={e => setForm({ ...form, number_of_operators: e.target.value })}
                    placeholder="e.g. 1"
                    className="font-mono mt-0.5"
                    data-testid="pp-operators" />
                </div>
              </div>
            </div>

            {/* Live preview of derived values */}
            <div className="rounded-lg border-2 border-[#065F46]/30 bg-[#ECFDF5] p-3">
              <div className="flex items-center gap-2 mb-2">
                <Calculator size={14} className="text-[#065F46]" />
                <h4 className="text-[10px] font-bold uppercase tracking-widest text-[#065F46]">
                  Auto-Calculated
                </h4>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] text-[#065F46] uppercase font-semibold">Output per Hour</div>
                  <div className="text-xl font-black font-mono text-[#065F46]" data-testid="pp-preview-oph">
                    {nfmt(outputPerHour, 2)}
                  </div>
                  <div className="text-[10px] text-[#065F46]/70 mt-0.5">
                    (60 ÷ Cycle Time) × Efficiency
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-[#7C2D12] uppercase font-semibold">Cost of Operation (₹/pc)</div>
                  <div className="text-xl font-black font-mono text-[#7C2D12]" data-testid="pp-preview-cost">
                    ₹ {nfmt(costOfOperation, 2)}
                  </div>
                  <div className="text-[10px] text-[#7C2D12]/70 mt-0.5">
                    Machine Hour Rate ÷ Output per Hour
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[#E2E8F0] mt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#1D2951] text-white" onClick={save} data-testid="pp-save">
              <Plus size={14} /> {editingId ? "Save Changes" : "Add Plan"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={opDialogOpen} onOpenChange={setOpDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add Operation</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-1">
            <div>
              <Label className="text-[11px] font-semibold text-[#334155]">Name *</Label>
              <Input value={newOpName}
                onChange={e => setNewOpName(e.target.value)}
                placeholder="e.g. Operation 11, Heat Treatment, Deburring"
                className="mt-0.5"
                data-testid="pp-new-op-name" />
              <p className="text-[10px] text-[#94A3B8] mt-1">
                Operation 1..10 are seeded by default. Add your own here.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setOpDialogOpen(false)}>Cancel</Button>
              <Button className="bg-[#1D2951] text-white" onClick={addOperation} data-testid="pp-new-op-save">
                <Plus size={14} /> Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
